import { useState } from "react";
import { Check, X, Pencil, MapPin, Calendar } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "./StatusBadge";
import { EventDialog } from "./EventDialog";
import { useUpdateEventStatus } from "@/hooks/useEvents";
import { toast } from "sonner";
import type { EventResponse, Category } from "@/types";

function formatDate(dateStr: string) {
  return new Intl.DateTimeFormat("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(dateStr));
}

interface EventCardProps {
  event: EventResponse;
  categories: Category[];
}

export function EventCard({ event, categories }: EventCardProps) {
  const updateStatus = useUpdateEventStatus();
  const [editOpen, setEditOpen] = useState(false);
  const category = categories.find((c) => c.id === event.categoryId);

  const handleApprove = async () => {
    try {
      await updateStatus.mutateAsync({ id: event.id, status: "approved" });
      toast.success("Wydarzenie zatwierdzone");
    } catch (err) {
      toast.error("Nie udało się zatwierdzić wydarzenia", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleReject = async () => {
    try {
      await updateStatus.mutateAsync({ id: event.id, status: "rejected" });
      toast.success("Wydarzenie odrzucone");
    } catch (err) {
      toast.error("Nie udało się odrzucić wydarzenia", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <>
      <div className="bg-white rounded-xl overflow-hidden flex flex-col shadow-sm border border-black/5 hover:shadow-md transition-shadow">
        {event.coverImage ? (
          <img src={event.coverImage.full} alt={event.title} className="h-44 w-full object-cover" />
        ) : (
          <div className="h-44 w-full flex items-center justify-center text-sm font-medium bg-pulso-bg text-pulso-muted">
            Brak zdjęcia
          </div>
        )}
        <div className="flex-1 flex flex-col p-4 gap-3">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-sm leading-snug flex-1">{event.title}</h3>
            <StatusBadge status={event.status} rejectionReason={event.rejectionReason} />
          </div>

          {category && (
            <span
              className="self-start text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: `${category.colorHex}20`,
                color: category.colorHex,
              }}
            >
              {category.name}
            </span>
          )}

          <div className="space-y-1 text-xs text-pulso-muted">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 shrink-0" />
              <span>{formatDate(event.date)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{event.locationName}</span>
            </div>
          </div>

          {event.eventDescription && (
            <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
              {event.eventDescription}
            </p>
          )}

          <div className="flex gap-2 mt-auto pt-1">
            <Button
              size="sm"
              className="flex-1 h-8 text-xs font-semibold text-white bg-pulso-success hover:bg-pulso-success/90"
              onClick={handleApprove}
              disabled={updateStatus.isPending}
            >
              {updateStatus.isPending ? (
                <Spinner className="h-3.5 w-3.5 mr-1" />
              ) : (
                <Check className="h-3.5 w-3.5 mr-1" />
              )}
              Zatwierdź
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="flex-1 h-8 text-xs font-semibold"
              onClick={handleReject}
              disabled={updateStatus.isPending}
            >
              {updateStatus.isPending ? (
                <Spinner className="h-3.5 w-3.5 mr-1" />
              ) : (
                <X className="h-3.5 w-3.5 mr-1" />
              )}
              Odrzuć
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0 shrink-0"
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
      <EventDialog event={event} open={editOpen} onOpenChange={setEditOpen} />
    </>
  );
}
